import { z } from "zod";

import { PROJECT_SUPPLY_QUANTITY_UNITS } from "../schemas/project_supplies";

/**
 * Project validation schemas for the wizard and forms
 */

const investmentResourceSourceOptions = ["bndes", "other_sources"] as const;
const investmentProgramOptions = [
  "pronamp",
  "inovagro",
  "proirriga",
  "pca",
  "renovagro",
  "demais_investimentos",
] as const;
const projectTypeOptions = ["agricultural", "livestock"] as const;
const technicalProjectTypeOptions = ["operating_cost", "investment"] as const;
const emptyStringAsUndefined = z.literal("").transform(() => undefined);
const emptyOptionalString = z.union([z.undefined(), emptyStringAsUndefined]);

// Basic project form schema for wizard step 1
const sharedProjectBasicFormSchema = z.object({
  selectedProducerId: z.string().min(1, "Produtor é obrigatório"),
  technicalProjectType: z.enum(technicalProjectTypeOptions, {
    message: "Tipo de projeto técnico é obrigatório",
  }),
  bankId: z.string().min(1, "Banco é obrigatório"),
  producerClassificationId: z.string().min(1, "Enquadramento do produtor é obrigatório"),
});

const operatingCostProjectBasicFormSchema = sharedProjectBasicFormSchema
  .extend({
    technicalProjectType: z.literal("operating_cost"),
    projectType: z.enum(projectTypeOptions, {
      message: "Tipo do projeto é obrigatório",
    }),
    activityId: z.string().min(1, "Atividade é obrigatória"),
    insuranceModalityId: z.string().optional(),
    harvest: z.string({ error: "Safra é obrigatória" }).trim().min(1, "Safra é obrigatória"),
  })
  .superRefine((value, ctx) => {
    if (value.projectType === "agricultural" && !value.insuranceModalityId) {
      ctx.addIssue({
        code: "custom",
        message: "Modalidade do seguro é obrigatória",
        path: ["insuranceModalityId"],
      });
    }
  });

const investmentProjectBasicFormSchema = sharedProjectBasicFormSchema.extend({
  technicalProjectType: z.literal("investment"),
  investmentResourceSource: z.enum(investmentResourceSourceOptions, {
    message: "Fonte de recurso é obrigatória",
  }),
  programCode: z.union([emptyStringAsUndefined, z.enum(investmentProgramOptions)]).optional(),
  projectType: emptyOptionalString,
  activityId: emptyOptionalString,
  insuranceModalityId: emptyOptionalString,
  harvest: emptyOptionalString,
});

export const projectBasicFormSchema = z.union([
  operatingCostProjectBasicFormSchema,
  investmentProjectBasicFormSchema,
]);

export type ProjectBasicFormData = z.infer<typeof projectBasicFormSchema>;

export const InventorySnapshotFormSchema = z.object({
  id: z.string().optional(),
  description: z.string().min(1, "Descrição é obrigatória"),
  quantity: z.number().min(0, "Quantidade deve ser positiva"),
  unit: z.enum(["kg", "boxes", "liters", "hectare", "bag", "ton", "unit"]),
  unitPrice: z.number().min(0, "Preço unitário deve ser positivo"),
  totalValue: z.number().min(0, "Valor total deve ser positivo"),
  totalInventoryValue: z.number().min(0, "Valor total do inventário deve ser positivo").optional(),
  supplier: z.string().optional(),
  purchaseDate: z.number().optional(), // timestamp
  expirationDate: z.number().optional(), // timestamp
  location: z.string().optional(),
  notes: z.string().optional(),
});

export const ProjectProductionInvestmentFormSchema = z.object({
  // Investment information
  estimatedTotalInvestment: z
    .number()
    .min(1, "Investimento total deve ser maior que zero")
    .optional(),
  fundingSource: z
    .enum(["own", "loan", "grant", "mixed"], {
      message: "Selecione uma fonte de financiamento válida",
    })
    .optional(),
  loanAmount: z.number().min(0, "Valor do empréstimo deve ser maior ou igual a zero").optional(),
  grantAmount: z.number().min(0, "Valor do subsídio deve ser maior ou igual a zero").optional(),
  investmentReturnPeriodMonths: z
    .number()
    .int("Período de retorno deve ser um número inteiro")
    .min(1, "Período de retorno deve ser maior que zero")
    .max(360, "Período de retorno deve ser menor que 360 meses")
    .optional(),
  expectedRoiPercentage: z
    .number()
    .min(0, "ROI deve ser maior ou igual a zero")
    .max(999.99, "ROI deve ser menor que 1000%")
    .optional(),
  projectDurationMonths: z
    .number()
    .int("Duração do projeto deve ser um número inteiro")
    .min(1, "Duração do projeto deve ser maior que zero")
    .max(120, "Duração do projeto deve ser menor que 120 meses")
    .optional(),

  // Production information
  mainCropType: z
    .string()
    .max(100, "Tipo de cultura principal deve ter no máximo 100 caracteres")
    .optional(),
  totalProductionArea: z
    .number()
    .min(0.01, "Área total de produção deve ser maior que zero")
    .max(999_999.99, "Área total de produção deve ser menor que 1.000.000 hectares")
    .optional(),
  expectedTotalProduction: z
    .number()
    .min(0.01, "Produção total esperada deve ser maior que zero")
    .optional(),

  // String fields for form input handling
  estimatedTotalInvestmentString: z.string().optional(),
  loanAmountString: z.string().optional(),
  grantAmountString: z.string().optional(),
  investmentReturnPeriodMonthsString: z.string().optional(),
  expectedRoiPercentageString: z.string().optional(),
  projectDurationMonthsString: z.string().optional(),
  totalProductionAreaString: z.string().optional(),
  expectedTotalProductionString: z.string().optional(),
});

export const ProjectBenefitedAreaForm = z.object({
  id: z.string().optional(),
  areaHectares: z.number().min(0.01, "Área deve ser maior que zero"),
  cultureType: z.string().min(1, "Tipo de cultura é obrigatório"),
  variety: z.string().optional(),
  soilType: z.string().optional(),
  topography: z.enum(["flat", "hilly", "mountainous", "mixed"]).optional(),
  irrigationType: z.enum(["none", "sprinkler", "drip", "flood", "pivot"]).optional(),
  previousCrop: z.string().optional(),
  producerRuralPropertyId: z.string().optional(),
  notes: z.string().optional(),
});

export const CropSeasonForm = z.object({
  id: z.string().optional(),
  seasonName: z.string().min(1, "Nome da safra é obrigatório"),
  startDate: z.number(), // timestamp
  endDate: z.number(), // timestamp
  cultureType: z.string().min(1, "Tipo de cultura é obrigatório"),
  variety: z.string().optional(),
  areaHectares: z.number().min(0.01, "Área deve ser maior que zero"),
  expectedProduction: z.number().min(0, "Produção esperada deve ser positiva").optional(),
  actualProduction: z.number().min(0, "Produção real deve ser positiva").optional(),
  status: z.enum(["planned", "active", "completed", "cancelled"]).default("planned"),
  notes: z.string().optional(),
});

export const ProjectServicesForm = z.object({
  id: z.string().optional(),
  serviceName: z.string().min(1, "Nome do serviço é obrigatório"),
  description: z.string().optional(),
  provider: z.string().optional(),
  category: z.enum(["consulting", "implementation", "maintenance", "analysis", "other"]),
  unit: z.enum(["hour", "day", "hectare", "project", "other"]),
  quantity: z.number().min(0.01, "Quantidade deve ser maior que zero"),
  unitPrice: z.number().min(0, "Preço unitário deve ser positivo"),
  totalValue: z.number().min(0, "Valor total deve ser positivo"),
  scheduledDate: z.number().optional(), // timestamp
  completionDate: z.number().optional(), // timestamp
  status: z.enum(["scheduled", "in_progress", "completed", "cancelled"]).default("scheduled"),
  notes: z.string().optional(),
});

export const ProjectSuppliesForm = z.object({
  id: z.string().optional(),
  supplyName: z.string().min(1, "Nome do insumo é obrigatório"),
  description: z.string().optional(),
  category: z.enum(["seed", "fertilizer", "pesticide", "fuel", "equipment", "other"]),
  brand: z.string().optional(),
  supplier: z.string().optional(),
  unit: z.enum(["kg", "boxes", "liters", "hectare", "bag", "ton", "unit"]),
  quantity: z.number().min(0.01, "Quantidade deve ser maior que zero"),
  unitPrice: z.number().min(0, "Preço unitário deve ser positivo"),
  totalValue: z.number().min(0, "Valor total deve ser positivo"),
  applicationMethod: z.string().optional(),
  applicationDate: z.number().optional(), // timestamp
  applicationRate: z.string().optional(),
  status: z.enum(["planned", "ordered", "delivered", "applied"]).default("planned"),
  notes: z.string().optional(),
});

export const ProjectInputRecommendationsForm = z.object({
  id: z.string().optional(),
  soilTypeId: z.string().min(1, "Classificação do solo é obrigatória"),
  inputManagementTypeId: z.string().min(1, "Nível de manejo é obrigatório"),
  productionCycleId: z.string().min(1, "Ciclo da cultivar é obrigatório"),
  recommendations: z.string().min(1, "Recomendações são obrigatórias"),
});

export const ProjectTechnicalReportForm = z.object({
  technicalReport: z.string().min(1, "Parecer técnico é obrigatório"),
});

export const ProjectInvestmentPurposeForm = z.object({
  investmentPurposeDescription: z.string().min(1, "Finalidade do investimento é obrigatória"),
});

export const ProjectTechnicalAspectsForm = z.object({
  technicalAspects: z.string().min(1, "Aspectos técnicos são obrigatórios"),
});

export const ProjectWorkflowStatusForm = z.object({
  workflowStatus: z.enum(
    ["emission_in_progress", "issued", "reviewed", "pending_regularization"] as const,
    {
      message: "Selecione um andamento válido",
    },
  ),
  note: z.string().optional(),
});

export const ProjectEnvironmentalRegularityForm = z.object({
  activityIsLicensed: z.boolean(),
  activityLicensedExpirationDate: z.number().optional(), // timestamp
  environmentalLicenseNumber: z.string().optional(),
  environmentalDispensationType: z.enum(["individual", "generic"]).optional(),
  environmentalNormativeNumber: z.string().optional(),
  activityIsLicensedJustification: z.string(),
});

export const activityViabilityFormSchema = z.object({
  useProducerViability: z.boolean(),
  useProducerViabilityOptions: z.enum(["yes", "no"]),
  otherRevenue: z.number().min(0, "Outras receitas devem ser positivas"),
  otherRevenueString: z.string(),
  rentalValue: z.number().min(0, "Valor do arrendamento deve ser positivo"),
  rentalValueString: z.string(),
  familyMaintenanceValue: z.number().min(0, "Valor da manutenção familiar deve ser positivo"),
  familyMaintenanceValueString: z.string(),
  freightValue: z.number().min(0, "Valor do frete deve ser positivo"),
  freightValueString: z.string(),
  taxesValue: z.number().min(0, "Valor das taxas deve ser positivo"),
  taxesValueString: z.string(),
  otherValue: z.number().min(0, "Outros valores devem ser positivos"),
  otherValueString: z.string(),
});

export const ProjectDisbursementScheduleForm = z.object({
  totalFinancedValue: z.number().min(0, "Valor total financiado deve ser positivo"),
  parcels: z.array(
    z.object({
      id: z.string().optional(),
      parcelNumber: z.number().min(1, "Parcela inválida"),
      releaseDate: z.number().min(1, "Data de liberação é obrigatória"),
      releasedValue: z.number().min(0.01, "Valor liberado deve ser maior que zero"),
      percentage: z.number().min(0.01, "Percentual deve ser maior que zero"),
    }),
  ),
});

/**
 * Production System configuration form (Project details)
 * Used by the web app to validate the Production System tab before submitting
 */
export const ProjectProductionSystemForm = z.object({
  typeOfAgricultureId: z.string().optional(),
  typeOfIntegrationOrConsortiumId: z.string().optional(),
  grainOrSeedsId: z.string().optional(),
  typeOfIrrigationId: z.string().optional(),
  typeOfCultivationId: z.string().optional(),
  typeOfClimateId: z.string().optional(),
  productionCycleOrCreationPhaseId: z.string().optional(),
  managementPracticeId: z.string().optional(),
  productionSystemCode: z.string().optional(),
});

export type ProjectProductionSystemFormType = z.infer<typeof ProjectProductionSystemForm>;

export const projectOtherExpenseFormSchema = z.object({
  insuranceModalityId: z.string().optional(),
  insurancePolicyCode: z.string(),
  insuranceTotalValue: z.number().min(0, "Valor total deve ser positivo"),
  insuranceOwnResourcesValue: z.number().min(0, "Recursos próprios devem ser positivos"),
  insuranceTotalValueString: z.string(),
  insuranceOwnResourcesValueString: z.string(),
});

export const astecFormSchema = z
  .object({
    astecPercentual: z
      .number()
      .min(0, "Percentual ASTEC deve ser positivo")
      .max(4, "Percentual ASTEC deve ser menor ou igual a 4"),
    astecPercentualString: z.string(),
    astecTechnicalAssistance: z.boolean(),
    astecTechnicalAssistanceOptions: z.enum(["yes", "no"]),
    astecWillBeFunded: z.boolean(),
    astecWillBeFundedOptions: z.enum(["yes", "no"]),
    astecHasCheckingAccount: z.boolean(),
    astecHasCheckingAccountOptions: z.enum(["yes", "no"]),
  })
  .superRefine((value, ctx) => {
    const requiresTechnicalAssistance = value.astecPercentual > 0.5 && value.astecPercentual < 2;

    if (requiresTechnicalAssistance && !value.astecTechnicalAssistance) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Para percentual de ASTEC maior que 0,5% e menor que 2%, o serviço de Assistência Técnica é obrigatório.",
        path: ["astecTechnicalAssistanceOptions"],
      });
    }
  });

export type AstecSchema = z.infer<typeof astecFormSchema>;

export const framingFormSchema = z.object({
  producerClassificationId: z.string().optional(),
  harvest: z.string().min(1, "Safra é obrigatória"),
  producerRevenue: z.number().min(0, "Renda do produtor deve ser positiva").optional(),
  producerRevenueString: z.string().optional(),
});

export type FramingSchema = z.infer<typeof framingFormSchema>;

export const projectInsuranceFormSchema = z.object({
  insuranceModalityId: z.string().min(1, "Modalidade do seguro é obrigatória"),
  insuranceWillBeFunded: z.boolean(),
  insuranceWillBeFundedOptions: z.enum(["yes", "no"]),
  insurancePolicyCode: z.string().optional(),
  insurancePolicyDate: z.number().optional(), // timestamp
  insuranceCompany: z.string().optional(),
  insuranceTypeId: z.string().optional(),
});

export type InsuranceSchema = z.infer<typeof projectInsuranceFormSchema>;

export const cultureActivityFormSchema = z.object({
  expectedProductivity: z.number().min(0, "Produtividade esperada deve ser positiva"),
  expectedProductivityString: z.string(),
  expectedProductivityUnit: z.enum([
    "dozen",
    "litre",
    "cubic_metre",
    "thousand_seedlings",
    "kilogram",
    "ton",
  ]),
  expectedSalePrice: z.number().min(0, "Preço de venda deve ser positivo"),
  expectedSalePriceString: z.string(),
  expectedGrossRevenue: z.number().min(0, "Receita bruta deve ser positiva"),
  expectedGrossRevenueString: z.string(),
});

export type CultureActivitySchema = z.infer<typeof cultureActivityFormSchema>;

// Export types for TypeScript
export type InventorySnapshotFormType = z.infer<typeof InventorySnapshotFormSchema>;
export type ProjectProductionInvestmentFormType = z.infer<
  typeof ProjectProductionInvestmentFormSchema
>;
export type ProjectBenefitedAreaFormType = z.infer<typeof ProjectBenefitedAreaForm>;
export type CropSeasonFormType = z.infer<typeof CropSeasonForm>;
export type ProjectServicesFormType = z.infer<typeof ProjectServicesForm>;
export type ProjectSuppliesFormType = z.infer<typeof ProjectSuppliesForm>;
export type ProjectInputRecommendationsFormType = z.infer<typeof ProjectInputRecommendationsForm>;
export type ProjectTechnicalReportFormType = z.infer<typeof ProjectTechnicalReportForm>;
export type ProjectInvestmentPurposeFormType = z.infer<typeof ProjectInvestmentPurposeForm>;
export type ProjectTechnicalAspectsFormType = z.infer<typeof ProjectTechnicalAspectsForm>;
export type ProjectEnvironmentalRegularityFormType = z.infer<
  typeof ProjectEnvironmentalRegularityForm
>;
export type ActivityViabilityFormType = z.infer<typeof activityViabilityFormSchema>;
export type ProjectDisbursementScheduleFormType = z.infer<typeof ProjectDisbursementScheduleForm>;
export type ProjectOtherExpenseFormSchema = z.infer<typeof projectOtherExpenseFormSchema>;

// Project Supplies Expected Acquisition Type
export const projectSuppliesExpectedAcquisitionTypeEnum = ["immediate", "planned"] as const;

export const projectSuppliesExpectedAcquisitionTypeMap = {
  immediate: "Imediato",
  planned: "Planejado",
} as const;

// Unit of Measurement — must stay aligned with project_supplies schema / addProjectSupply
export const unitOfMeasurementEnum = PROJECT_SUPPLY_QUANTITY_UNITS;

export const unitOfMeasurementMap = {
  kg: "Quilograma",
  l: "Litro",
  m3: "Metro cúbico",
  unit: "Unidade",
  pack: "Pacote",
  ton: "Tonelada",
} as const satisfies Record<(typeof PROJECT_SUPPLY_QUANTITY_UNITS)[number], string>;
